import React from 'react';
import { Helmet } from 'react-helmet-async';
import { seoConfig, generateTitle, generateDescription, generateCanonicalUrl } from '../../config/seoConfig';

const SEOHead = ({
  title,
  description,
  image,
  url,
  type = 'website',
  keywords,
  product,
  breadcrumbs,
  noindex = false
}) => {
  const pageTitle = generateTitle(title);
  const pageDescription = generateDescription(description);
  const pageImage = image || seoConfig.defaultImage;
  const canonicalUrl = url ? generateCanonicalUrl(url) : seoConfig.siteUrl;
  const pageKeywords = keywords ? [...seoConfig.keywords, ...keywords].join(', ') : seoConfig.keywords.join(', ');

  // Schema.org Organization (présent sur toutes les pages)
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${seoConfig.siteUrl}/#organization`,
    name: seoConfig.organization.name,
    legalName: seoConfig.organization.legalName,
    url: seoConfig.organization.url,
    logo: {
      '@type': 'ImageObject',
      url: seoConfig.organization.logo
    },
    description: seoConfig.organization.description,
    email: seoConfig.organization.email,
    telephone: seoConfig.organization.telephone,
    address: seoConfig.organization.address,
    sameAs: seoConfig.organization.sameAs
  };

  // Schema.org WebSite (pour la page d'accueil)
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${seoConfig.siteUrl}/#website`,
    url: seoConfig.siteUrl,
    name: seoConfig.siteName,
    description: seoConfig.defaultDescription,
    publisher: {
      '@id': `${seoConfig.siteUrl}/#organization`
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: `${seoConfig.siteUrl}/recherche?q={search_term_string}`,
      'query-input': 'required name=search_term_string'
    }
  };

  // Schema.org Product (pour les pages produits)
  const productSchema = product ? {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description || pageDescription,
    image: product.image || pageImage,
    sku: product.ref_frs || product.id,
    brand: {
      '@type': 'Brand',
      name: product.supplier || seoConfig.siteName
    },
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/PreOrder',
      priceCurrency: 'EUR',
      url: canonicalUrl,
      seller: {
        '@id': `${seoConfig.siteUrl}/#organization`
      }
    }
  } : null;

  // Schema.org BreadcrumbList
  const breadcrumbSchema = breadcrumbs ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: `${seoConfig.siteUrl}${crumb.url}`
    }))
  } : null;

  // Compiler tous les schemas
  const schemas = [
    organizationSchema,
    url === '/' && websiteSchema,
    productSchema,
    breadcrumbSchema
  ].filter(Boolean);

  return (
    <Helmet>
      {/* Métadonnées de base */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Noindex si demandé (ex: pages admin) */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph (Facebook, LinkedIn) */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={seoConfig.siteName} />
      <meta property="og:locale" content="fr_FR" />
      
      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={seoConfig.twitterHandle} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />
      
      {/* Schema.org JSON-LD */}
      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEOHead;
